import type { ColumnSizingState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useWindowDimensions } from '../../helpers/window-dimension';
import type { StrictColumnDef } from './column';

/** Total horizontal padding on the table grid container (px-4 = 16px each side). */
export const TABLE_PADDING = 32;

/**
 * Compute responsive column widths based on window size.
 * Ports the existing VirtualizedTable layout algorithm to work with TanStack Table.
 *
 * Algorithm:
 * 1. All columns start at their minWidth
 * 2. Extra screen space is distributed proportionally to columns with maxWidth defined
 * 3. Distribution continues iteratively until budget exhausted or all columns capped
 */
export function useResponsiveColumnSizing<T>(columns: StrictColumnDef<T>[]): {
  columnSizing: ColumnSizingState;
  totalWidth: number;
  windowWidth: number;
} {
  const { width: windowWidth } = useWindowDimensions();

  return useMemo(() => {
    const PADDING = TABLE_PADDING;

    const getMinWidth = (col: StrictColumnDef<T>) => col.meta.minWidth;
    const getMaxWidth = (col: StrictColumnDef<T>) => col.meta.maxWidth;
    const getColumnId = (col: StrictColumnDef<T>) => col.id;

    const minimumWidth = columns.reduce((sum, col) => sum + getMinWidth(col), PADDING);

    const sizing: ColumnSizingState = {};

    // If window is smaller than minimum, use minWidth for all columns
    if (windowWidth < minimumWidth) {
      for (const col of columns) {
        sizing[getColumnId(col)] = getMinWidth(col);
      }

      return { columnSizing: sizing, totalWidth: minimumWidth, windowWidth };
    }

    // Initialize all columns at minWidth
    const widths = new Map<string, number>();
    for (const col of columns) {
      widths.set(getColumnId(col), getMinWidth(col));
    }

    // Find columns that can expand (have maxWidth defined)
    const expandableColumns = columns
      .map((col) => ({ col, id: getColumnId(col) }))
      .filter(({ col }) => getMaxWidth(col) !== undefined);

    if (expandableColumns.length === 0) {
      // No expandable columns, return all at minWidth
      for (const col of columns) {
        sizing[getColumnId(col)] = getMinWidth(col);
      }

      return { columnSizing: sizing, totalWidth: minimumWidth, windowWidth };
    }

    let remainingBudget = windowWidth - minimumWidth;

    // Iteratively distribute space until budget exhausted or all columns capped
    while (remainingBudget > 0.01) {
      // Find columns that haven't reached maxWidth yet
      const uncappedColumns = expandableColumns.filter(({ col, id }) => {
        const currentWidth = widths.get(id);
        const maxWidth = getMaxWidth(col);

        return currentWidth !== undefined && maxWidth !== undefined && currentWidth < maxWidth;
      });

      if (uncappedColumns.length === 0) {
        break;
      }

      // Calculate how much space each column can still take
      const availableSpace = uncappedColumns.reduce((sum, { col, id }) => {
        const currentWidth = widths.get(id);
        const maxWidth = getMaxWidth(col);
        if (currentWidth !== undefined && maxWidth !== undefined) {
          return sum + (maxWidth - currentWidth);
        }

        return sum;
      }, 0);

      // Only distribute what we can actually use
      const spaceToDistribute = Math.min(remainingBudget, availableSpace);
      if (spaceToDistribute <= 0) {
        break;
      }

      // Calculate total minWidth of uncapped columns for proportional distribution
      const totalMinWidth = uncappedColumns.reduce((sum, { col }) => sum + getMinWidth(col), 0);

      // Distribute space proportionally
      let budgetUsed = 0;
      for (const { col, id } of uncappedColumns) {
        const proportion = getMinWidth(col) / totalMinWidth;
        const idealAllocation = spaceToDistribute * proportion;
        const currentWidth = widths.get(id);
        const maxWidth = getMaxWidth(col);

        if (currentWidth !== undefined && maxWidth !== undefined) {
          const newWidth = currentWidth + idealAllocation;
          const cappedWidth = Math.min(newWidth, maxWidth);
          const actualAllocation = cappedWidth - currentWidth;

          widths.set(id, cappedWidth);
          budgetUsed += actualAllocation;
        }
      }

      remainingBudget -= budgetUsed;

      // If we didn't use any budget this iteration, we're done
      if (budgetUsed < 0.01) {
        break;
      }
    }

    // Build final sizing state
    for (const col of columns) {
      const id = getColumnId(col);
      // Columns without maxWidth stay at minWidth
      if (getMaxWidth(col) === undefined) {
        sizing[id] = getMinWidth(col);
      } else {
        sizing[id] = widths.get(id) ?? getMinWidth(col);
      }
    }

    // Calculate total width
    const totalWidth = Object.values(sizing).reduce((sum, width) => sum + width, PADDING);

    return { columnSizing: sizing, totalWidth, windowWidth };
  }, [columns, windowWidth]);
}
